import React, { useState, SyntheticEvent, useContext } from "react";
import { useRouter } from "next/router";
import trim from "lodash/trim";
import isEmpty from "lodash/isEmpty";

import { useTranslator } from "@eo-locale/react";

import Cookies from "js-cookie";

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useMediaQuery,
  FormControl
} from "@/storybook-components";
import UnverifiedFormTools from "@/components/unverified-user/modal/form";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  setError,
  setAnswer,
  clearAllErrors,
  resetAnswers
} from "@/redux/features/questions/slice";
import { createUnverifiedUser } from "@/redux/features/user/thunk";
import { EsriContextType } from "../../context/esri-context-type";
import { EsriContext } from "../../context/esri-context";
import useVotes from "@/hooks/places/use-votes";

const UnverifiedUserModal = ({ isOpen, onClose, handlePinCreation }) => {
  const [hasErrorInSubmission, setHasErrorInSubmission] = useState(false);

  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const [isDesktop] = useMediaQuery("(min-width: 768px)");
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { query } = useRouter();

  const { answers, errors } = useAppSelector((state) => state.questions);
  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);

  const { setInteractionsVisibility } = useContext(
    EsriContext
  ) as EsriContextType;

  const { fetchPinDetailsWithSelectedData } = useVotes();

  const validateAndDispatchError = async (field, errorType = "blank") => {
    if (!answers?.[field]) {
      await dispatch(setError({ questionId: field, error: [errorType] }));
    }
  };

  const onSubmitHandler = async (e: SyntheticEvent) => {
    e.preventDefault();

    let errorCount = 0;
    if (!answers?.email) {
      await validateAndDispatchError("email");
      errorCount += 1;
    }

    if (!answers?.login) {
      await validateAndDispatchError("login");
      errorCount += 1;
    }

    if (isEmpty(trim(answers?.login))) {
      await validateAndDispatchError("login");
      errorCount += 1;
    }

    if (!answers?.tosQuestion) {
      await validateAndDispatchError("tosQuestion", "accepted");
      errorCount += 1;
    }

    if (errors?.email) errorCount += 1;

    if (errorCount <= 0) {
      await dispatch(
        createUnverifiedUser({ projectId: query.projectId as string })
      );

      handleClose();
    }

    if (errorCount > 0) {
      setHasErrorInSubmission(true);
      return false;
    }

    await setHasErrorInSubmission(false);

    ["email", "login", "tosQuestion"].forEach(async (question) => {
      await dispatch(
        setAnswer({
          questionId: question,
          answer: answers?.[question]
        })
      );
    });

    if (!isEmpty(triggerPoint.triggeredSource)) {
      fetchPinDetailsWithSelectedData();
      return;
    }

    handlePinCreation();
  };

  const handleClose = () => {
    dispatch(onClose(false));
    Cookies.remove("trigger_point");
    ["email", "login", "checkbox"].forEach((questionId) => {
      dispatch(
        setAnswer({
          questionId,
          answer: questionId === "checkbox" ? false : ""
        })
      );
    });

    dispatch(clearAllErrors());
    dispatch(resetAnswers());

    if (isMobile) {
      setInteractionsVisibility(true);
    }
  };

  return (
    <>
      <Modal
        onClose={handleClose}
        isOpen={isOpen}
        size={isDesktop ? "md" : "full"}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{translate("brainstormers.haveYourSay")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl
              display="flex"
              flexDirection="column"
              gap={6}
              sx={{
                "> p": { margin: 0 },
                "> div": { margin: 0 },
                ".fr-view": {
                  fontSize: "sm",
                  fontWeight: "semibold",
                  color: "gray.800"
                },
                ".create-idea-form": {
                  fontWeight: "normal"
                },
                "& input, & textarea": {
                  background: "white"
                }
              }}
            >
              <UnverifiedFormTools
                hasErrorInSubmission={hasErrorInSubmission}
                isAsterixRequired={false}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blackAlpha"
              variant="secondary"
              width={{ base: "full", md: "auto" }}
              onClick={handleClose}
            >
              {translate("common.cancel")}
            </Button>
            <Button
              ml={4}
              width={{ base: "full", md: "auto" }}
              onClick={onSubmitHandler}
              variant="primary"
            >
              {translate("common.continue")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UnverifiedUserModal;
